import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useSubscriptions } from "../context/subscriptions-context";
import { getPrimaryWebsiteOffering, getSecondaryWebsiteOfferings } from "../offerings/registry";
import { useOfferings } from "../offerings/use-offerings";
import { FeedbackTrigger } from "./feedback-trigger";
import { AbExperimentOverlay } from "./ab-experiment-overlay";
import { SiteStructuredData } from "./site-structured-data";
import { SitePageSeo } from "./site-page-seo";
import { useAbExperiment } from "../hooks/use-ab-experiment";
import { usePageviewTracker } from "../hooks/use-analytics";
import { AbExperimentContext } from "@constructorfleet/offerings-sdk";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChunkLoadErrorBoundary } from "./chunk-error-boundary";
import { AnimatePresence, motion } from "framer-motion";
import { Button, IconSettings, IconLogOut, Spinner } from "@constructorfleet/ui/design-system";

function ProductsFlyout({
  offerings,
}: {
  offerings: ReturnType<typeof useOfferings>["offerings"];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, close]);

  return (
    <li className="nav-flyout" ref={ref}>
      <span
        className="nav-flyout-trigger"
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        Products
      </span>
      {open && (
        <div className="nav-flyout-menu" role="menu">
          {offerings.map((offering) => (
            <Link
              key={offering.id}
              to={`/${offering.routes.productPath}`}
              role="menuitem"
              onClick={close}
            >
              <span className="nav-flyout-item-label">{offering.name}</span>
              <span className="nav-flyout-item-desc">{offering.marketing.summary}</span>
            </Link>
          ))}
        </div>
      )}
    </li>
  );
}

export function AppLayout() {
  const { auth, logout } = useAuth();
  const { hasProduct } = useSubscriptions();
  const { offerings } = useOfferings();
  const navigate = useNavigate();
  const location = useLocation();
  const abExperiment = useAbExperiment({ enabled: Boolean(auth) });
  usePageviewTracker();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const primaryOffering = getPrimaryWebsiteOffering(offerings);
  const secondaryOfferings = getSecondaryWebsiteOfferings(offerings);
  const userPrimaryOfferingPath = primaryOffering
    ? hasProduct(primaryOffering.id)
      ? `/dashboard/${primaryOffering.id}`
      : `/${primaryOffering.routes.productPath}`
    : "/";
  const primaryOfferingPath = primaryOffering
    ? auth?.role === "admin"
      ? `/${primaryOffering.routes.adminSettingsPath}`
      : userPrimaryOfferingPath
    : "/";
  const primaryOfferingProductPath = primaryOffering
    ? `/${primaryOffering.routes.productPath}`
    : null;
  const pendingSignupPath =
    typeof (location.state as { from?: unknown } | null)?.from === "string"
      ? (location.state as { from: string }).from
      : null;
  const activePublicOffering = offerings.find((offering) => {
    const productPath = `/${offering.routes.productPath}`;
    const signupPath = offering.routes.signupPath ? `/${offering.routes.signupPath}` : null;
    return (
      location.pathname === productPath ||
      location.pathname.startsWith(`${productPath}/`) ||
      pendingSignupPath === signupPath
    );
  });
  const publicGetStartedPath = activePublicOffering?.routes.signupPath
    ? `/${activePublicOffering.routes.signupPath}`
    : "/pricing";
  const primaryOfferingTabIsActiveForUser =
    auth?.role === "user" &&
    Boolean(
      primaryOfferingProductPath &&
      (location.pathname === primaryOfferingProductPath ||
        location.pathname.startsWith(`${primaryOfferingProductPath}/`))
    );

  const abContextValue = useMemo(
    () => ({
      variant: abExperiment.variant,
      variantLabel: abExperiment.variantLabel,
      isLoading: abExperiment.isLoading,
      setOverride: abExperiment.setOverride,
      recordConversion: abExperiment.recordConversion,
    }),
    [
      abExperiment.variant,
      abExperiment.variantLabel,
      abExperiment.isLoading,
      abExperiment.setOverride,
      abExperiment.recordConversion,
    ]
  );

  return (
    <AbExperimentContext.Provider value={abContextValue}>
      <SiteStructuredData />
      <SitePageSeo />
      <div className="ds-site-frame">
        <header role="banner" aria-label="Application header" className="nav-header">
          <a className="skip-to-content" href="#main-content">
            Skip to content
          </a>
          <nav className="nav" aria-label="Primary">
            <div className="nav-inner">
              <Link to="/" className="nav-brand">
                <img src="/symbol-shield.webp" alt="" aria-hidden="true" width={19} height={22} />
                <span>ConstructorFleet</span>
              </Link>
              {!auth ? (
                <ul className="nav-links">
                  {offerings.length > 0 && (
                    <>
                      <ProductsFlyout offerings={offerings} />
                      {offerings.map((offering) => (
                        <li key={offering.id} className="nav-inline-only">
                          <NavLink
                            to={`/${offering.routes.productPath}`}
                            className={({ isActive }) => (isActive ? "active" : undefined)}
                          >
                            {offering.name}
                          </NavLink>
                        </li>
                      ))}
                    </>
                  )}
                  <li>
                    <NavLink
                      to="/about"
                      className={({ isActive }) => (isActive ? "active" : undefined)}
                    >
                      About
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/pricing"
                      className={({ isActive }) => (isActive ? "active" : undefined)}
                    >
                      Pricing
                    </NavLink>
                  </li>
                </ul>
              ) : (
                <ul className="nav-links">
                  <li>
                    <NavLink
                      to={primaryOfferingPath}
                      className={({ isActive }) =>
                        isActive || primaryOfferingTabIsActiveForUser ? "active" : undefined
                      }
                    >
                      {primaryOffering?.adminNav.navLabel ?? primaryOffering?.name ?? "Offering"}
                    </NavLink>
                  </li>
                  {secondaryOfferings.map((offering) => {
                    const productPath = `/${offering.routes.productPath}`;
                    const adminPath = `/${offering.routes.adminSettingsPath}`;
                    const dashboardPath = `/dashboard/${offering.id}`;
                    const tabActiveForUser =
                      auth?.role === "user" &&
                      (location.pathname === productPath ||
                        location.pathname.startsWith(`${productPath}/`));
                    const to =
                      auth?.role === "admin"
                        ? adminPath
                        : auth?.role === "user" && hasProduct(offering.id)
                          ? dashboardPath
                          : productPath;
                    return (
                      <li key={offering.id}>
                        <NavLink
                          to={to}
                          className={({ isActive }) =>
                            isActive || tabActiveForUser ? "active" : undefined
                          }
                        >
                          {offering.adminNav.navLabel ?? offering.name}
                        </NavLink>
                      </li>
                    );
                  })}
                  {auth?.role === "user" && (
                    <li>
                      <NavLink
                        end
                        to="/dashboard"
                        className={({ isActive }) => (isActive ? "active" : undefined)}
                      >
                        Dashboard
                      </NavLink>
                    </li>
                  )}
                  {auth?.role === "admin" && (
                    <li>
                      <NavLink
                        end
                        to="/dashboard"
                        className={({ isActive }) => (isActive ? "active" : undefined)}
                      >
                        Dashboard
                      </NavLink>
                    </li>
                  )}
                  {auth?.role === "admin" && (
                    <li>
                      <NavLink
                        to="/admin"
                        className={({ isActive }) => (isActive ? "active" : undefined)}
                      >
                        Admin
                      </NavLink>
                    </li>
                  )}
                  {auth?.role === "admin" && (
                    <li>
                      <NavLink
                        to="/ui-kit"
                        className={({ isActive }) => (isActive ? "active" : undefined)}
                      >
                        Design System
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
              <div className="nav-right">
                {auth ? (
                  <>
                    <span className="session-username">{auth.username}</span>
                    <button
                      type="button"
                      className="btn-icon"
                      aria-label="Account"
                      onClick={() => navigate("/settings")}
                    >
                      <IconSettings />
                    </button>
                    <button
                      type="button"
                      className="btn-icon"
                      aria-label="Log out"
                      onClick={logout}
                    >
                      <IconLogOut />
                    </button>
                  </>
                ) : (
                  <>
                    <Button as="a" href="/login" variant="ghost" size="sm">
                      Log in
                    </Button>
                    <Button as="a" href={publicGetStartedPath} variant="primary" size="sm">
                      Get started
                    </Button>
                  </>
                )}
                <button
                  type="button"
                  className="nav-mobile-toggle btn-icon"
                  aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileNavOpen}
                  aria-controls="mobile-nav-drawer"
                  onClick={() => setMobileNavOpen((v) => !v)}
                >
                  <span className={`nav-hamburger-icon ${mobileNavOpen ? "is-open" : ""}`} />
                </button>
              </div>
            </div>
          </nav>
          {mobileNavOpen && (
            <div className="nav-mobile-overlay" onClick={closeMobileNav}>
              <nav
                id="mobile-nav-drawer"
                className="nav-mobile-drawer"
                aria-label="Navigation menu"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("a")) closeMobileNav();
                }}
              >
                {!auth ? (
                  <ul>
                    {offerings.map((offering) => (
                      <li key={offering.id}>
                        <Link to={`/${offering.routes.productPath}`}>{offering.name}</Link>
                      </li>
                    ))}
                    <li>
                      <Link to="/about">About</Link>
                    </li>
                    <li>
                      <Link to="/pricing">Pricing</Link>
                    </li>
                    <li className="nav-mobile-auth-divider" />
                    <li>
                      <Link to="/login">Log in</Link>
                    </li>
                    <li>
                      <Link to={publicGetStartedPath} className="nav-mobile-cta">
                        Get started
                      </Link>
                    </li>
                  </ul>
                ) : (
                  <ul>
                    <li>
                      <Link to={primaryOfferingPath}>
                        {primaryOffering?.adminNav.navLabel ?? primaryOffering?.name ?? "Offering"}
                      </Link>
                    </li>
                    {secondaryOfferings.map((offering) => {
                      const productPath = `/${offering.routes.productPath}`;
                      const adminPath = `/${offering.routes.adminSettingsPath}`;
                      const dashboardPath = `/dashboard/${offering.id}`;
                      const to =
                        auth?.role === "admin"
                          ? adminPath
                          : auth?.role === "user" && hasProduct(offering.id)
                            ? dashboardPath
                            : productPath;
                      return (
                        <li key={offering.id}>
                          <Link to={to}>{offering.adminNav.navLabel ?? offering.name}</Link>
                        </li>
                      );
                    })}
                    {auth?.role === "user" && (
                      <li>
                        <Link to="/dashboard">Dashboard</Link>
                      </li>
                    )}
                    {auth?.role === "admin" && (
                      <>
                        <li>
                          <Link to="/dashboard">Dashboard</Link>
                        </li>
                        <li>
                          <Link to="/admin">Admin</Link>
                        </li>
                        <li>
                          <Link to="/ui-kit">Design System</Link>
                        </li>
                      </>
                    )}
                    <li className="nav-mobile-auth-divider" />
                    <li>
                      <Link to="/settings">Settings</Link>
                    </li>
                    <li>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          logout();
                        }}
                      >
                        Log out
                      </a>
                    </li>
                  </ul>
                )}
              </nav>
            </div>
          )}
        </header>

        <main id="main-content">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              className="route-stage"
              key={`route-stage:${location.pathname}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              <ChunkLoadErrorBoundary>
                <Suspense
                  fallback={
                    <div className="cf-loading-block">
                      <Spinner size="sm" />
                      Loading...
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </ChunkLoadErrorBoundary>
            </motion.div>
          </AnimatePresence>
          {auth && <FeedbackTrigger key={`feedback-trigger:${location.pathname}`} />}
          <AbExperimentOverlay
            variant={abExperiment.variant}
            assignment={abExperiment.assignment}
            setOverride={abExperiment.setOverride}
          />
        </main>

        <footer className="footer" aria-label="Site footer">
          <div className="container">
            <div className="footer-grid">
              <div>
                <div className="footer-brand">
                  <img src="/symbol-shield.webp" alt="" aria-hidden="true" width={21} height={24} />
                  ConstructorFleet
                </div>
                <p className="footer-brand-tag">
                  Reliable automation for the daily obligations that can't afford to be missed.
                </p>
              </div>
              <div>
                <h2>Products</h2>
                <ul>
                  {offerings.map((offering) => (
                    <li key={offering.id}>
                      <Link to={`/${offering.routes.productPath}`}>{offering.name}</Link>
                    </li>
                  ))}
                  <li>
                    <Link to="/pricing">Pricing</Link>
                  </li>
                </ul>
              </div>
              <div>
                <h2>Company</h2>
                <ul>
                  <li>
                    <Link to="/about">About Us</Link>
                  </li>
                  <li>
                    <a href="mailto:vogons@constructorfleet.com">Contact Us</a>
                  </li>
                </ul>
              </div>
              <div>
                <h2>Legal</h2>
                <ul>
                  <li>
                    <Link to="/terms">Terms of Service</Link>
                  </li>
                  <li>
                    <Link to="/privacy">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/refunds">Refund Policy</Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <span>
                &copy; {new Date().getFullYear()} ConstructorFleet L.L.C. All rights reserved.
              </span>
              <div className="nav-bits">
                <Link to="/">Home</Link>
                <Link to="/about">About</Link>
                <Link to="/pricing">Pricing</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </AbExperimentContext.Provider>
  );
}
